"use strict";
/**
 * Inventory Session Enums
 *
 * Enums for the persistent multi-pass inventory count workflow.
 * Sessions track physical inventory counts across multiple uploads
 * until closure is detected from QVET movements sync.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.COLLABORATOR_ACTION_LABELS = exports.CollaboratorAction = exports.MANAGER_DECISION_LABELS = exports.ManagerDecision = exports.TERMINAL_ITEM_STATUSES = exports.INVENTORY_ITEM_STATUS_LABELS = exports.InventoryItemStatus = exports.ACTIVE_SESSION_STATUSES = exports.EDITABLE_SESSION_STATUSES = exports.INVENTORY_SESSION_STATUS_LABELS = exports.InventorySessionStatus = void 0;
// --- Session Status ---
/**
 * Lifecycle status of an inventory session.
 *
 * counting → reviewing → validating → exported → closed
 *
 * - counting: Session created, physical counting in progress, no uploads yet
 * - reviewing: First upload done, collaborator reviewing counts
 * - validating: Collaborator done, manager validating/deciding
 * - exported: Final sheet downloaded, data propagated to CatalogItem
 * - closed: QVET import detected via movement sync
 */
var InventorySessionStatus;
(function (InventorySessionStatus) {
    InventorySessionStatus["counting"] = "counting";
    InventorySessionStatus["reviewing"] = "reviewing";
    InventorySessionStatus["validating"] = "validating";
    InventorySessionStatus["exported"] = "exported";
    InventorySessionStatus["closed"] = "closed";
})(InventorySessionStatus || (exports.InventorySessionStatus = InventorySessionStatus = {}));
exports.INVENTORY_SESSION_STATUS_LABELS = {
    [InventorySessionStatus.counting]: "Contando",
    [InventorySessionStatus.reviewing]: "En revisión",
    [InventorySessionStatus.validating]: "Validando",
    [InventorySessionStatus.exported]: "Exportado",
    [InventorySessionStatus.closed]: "Cerrado",
};
/** Statuses where editing (upload, count edits, collaborator actions) is allowed */
exports.EDITABLE_SESSION_STATUSES = new Set([
    InventorySessionStatus.counting,
    InventorySessionStatus.reviewing,
]);
/** Statuses where the session is still active (downloads, refresh, manager decisions) */
exports.ACTIVE_SESSION_STATUSES = new Set([
    InventorySessionStatus.counting,
    InventorySessionStatus.reviewing,
    InventorySessionStatus.validating,
]);
// --- Item Status (unified — single source of truth for display) ---
/**
 * Status of an individual item within a session.
 * This is THE field to display. No priority logic needed.
 *
 * Terminal states (session closeable): ok, tolerated, loss, justified
 * Non-terminal: pending, needs_review, verify_qvet, confirmed, recount
 */
var InventoryItemStatus;
(function (InventoryItemStatus) {
    // Auto-calculated from count data
    InventoryItemStatus["pending"] = "pending";
    InventoryItemStatus["ok"] = "ok";
    InventoryItemStatus["tolerated"] = "tolerated";
    InventoryItemStatus["needs_review"] = "needs_review";
    InventoryItemStatus["verify_qvet"] = "verify_qvet";
    // Set by collaborator
    InventoryItemStatus["confirmed"] = "confirmed";
    // Set by manager
    InventoryItemStatus["recount"] = "recount";
    // Auto-set after recount (still has discrepancy)
    InventoryItemStatus["recounted"] = "recounted";
    InventoryItemStatus["loss"] = "loss";
    InventoryItemStatus["justified"] = "justified";
})(InventoryItemStatus || (exports.InventoryItemStatus = InventoryItemStatus = {}));
exports.INVENTORY_ITEM_STATUS_LABELS = {
    [InventoryItemStatus.pending]: "Pendiente",
    [InventoryItemStatus.ok]: "OK",
    [InventoryItemStatus.tolerated]: "Tolerado",
    [InventoryItemStatus.needs_review]: "Revisar",
    [InventoryItemStatus.verify_qvet]: "Verificar QVET",
    [InventoryItemStatus.confirmed]: "Confirmado",
    [InventoryItemStatus.recount]: "Recontar",
    [InventoryItemStatus.recounted]: "Recontado",
    [InventoryItemStatus.loss]: "Pérdida",
    [InventoryItemStatus.justified]: "Justificado",
};
/** Terminal statuses — session can close when all items are in one of these */
exports.TERMINAL_ITEM_STATUSES = new Set([
    InventoryItemStatus.ok,
    InventoryItemStatus.tolerated,
    InventoryItemStatus.loss,
    InventoryItemStatus.justified,
]);
// --- Manager Decision (audit field) ---
/**
 * Decision made by the manager/reviewer on a counted item.
 * Stored in managerDecision for audit. The status field reflects the current state.
 *
 * No "validated" — manager must choose loss or justified for differences.
 * Items that match (ok/tolerated) don't need manager action.
 */
var ManagerDecision;
(function (ManagerDecision) {
    ManagerDecision["loss"] = "loss";
    ManagerDecision["justified"] = "justified";
    ManagerDecision["recount"] = "recount";
})(ManagerDecision || (exports.ManagerDecision = ManagerDecision = {}));
exports.MANAGER_DECISION_LABELS = {
    [ManagerDecision.loss]: "Pérdida",
    [ManagerDecision.justified]: "Justificado",
    [ManagerDecision.recount]: "Recontar",
};
// --- Collaborator Action (audit field) ---
/** Action taken by the collaborator on their own count */
var CollaboratorAction;
(function (CollaboratorAction) {
    CollaboratorAction["confirmed"] = "confirmed";
    CollaboratorAction["corrected"] = "corrected";
})(CollaboratorAction || (exports.CollaboratorAction = CollaboratorAction = {}));
exports.COLLABORATOR_ACTION_LABELS = {
    [CollaboratorAction.confirmed]: "Confirmado",
    [CollaboratorAction.corrected]: "Corregido",
};
